import { useCallback } from 'react';
import { toast } from 'sonner';
import { NodeType } from '../types';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { projectApi } from '../services/projectApi';
import { normalizeAssetUrl } from '../utils/assetUtils';
import { findClosestAspectRatio } from '../utils/aspectRatioUtils';

// 外部文件（拖拽 / 系统剪贴板粘贴）统一上传管线（方案 §4.1）
// 职责：给定 File[] + 屏幕落点 + 来源渠道，完成「过滤 → 建占位节点 → uploadAsset → 回填/降级」。
// 回填逻辑与 FlowEditor.handlePickerFileChange（节点菜单上传）完全一致，属纯抽取复用。

export type UploadChannel = 'drop' | 'paste';

const isSupportedType = (f: File) =>
  f.type.startsWith('image/') || f.type.startsWith('video/') || f.type.startsWith('audio/');

// 签名对齐 FlowEditorProps.onAddNode（FlowEditor.tsx:178，含可选 initialData）
interface UseExternalFileUploadParams {
  onAddNode: (type: NodeType, position?: { x: number; y: number }, initialData?: any) => string | undefined;
  screenToFlowPosition: (pos: { x: number; y: number }) => { x: number; y: number };
}

export function useExternalFileUpload({ onAddNode, screenToFlowPosition }: UseExternalFileUploadParams) {
  const uploadFilesAtScreen = useCallback(
    (files: File[], screenPos: { x: number; y: number }, channel: UploadChannel) => {
      const valid = files.filter(isSupportedType);
      const skipped = files.length - valid.length;
      if (skipped > 0) toast.warning(`已跳过 ${skipped} 个不支持的文件（仅支持图片/视频/音频）`);
      if (valid.length === 0) return;

      // 先同步建好所有占位节点（多文件错开 30px 防重叠，方案 §5 #2），再各自上传
      const tasks: Array<{ file: File; nodeId: string }> = [];
      valid.forEach((file, i) => {
        const flowPos = screenToFlowPosition({ x: screenPos.x + i * 30, y: screenPos.y + i * 30 });
        const nodeId = onAddNode(NodeType.MEDIA_UPLOAD, { x: flowPos.x, y: flowPos.y });
        if (nodeId) tasks.push({ file, nodeId });
      });

      tasks.forEach(({ file, nodeId }) => {
        const applyAsset = (url: string) => {
          if (file.type.startsWith('image/')) {
            const img = new Image();
            img.onload = () => {
              const aspectRatio = findClosestAspectRatio(img.width, img.height);
              useNodeStore.getState().updateNode(nodeId, {
                image: url,
                videoUri: undefined,
                audioUri: undefined,
                aspectRatio,
                isManualUpload: true,
              });
            };
            img.src = url;
          } else if (file.type.startsWith('video/')) {
            const video = document.createElement('video');
            video.onloadedmetadata = () => {
              const aspectRatio = findClosestAspectRatio(video.videoWidth, video.videoHeight);
              useNodeStore.getState().updateNode(nodeId, {
                videoUri: url,
                image: undefined,
                audioUri: undefined,
                aspectRatio,
                isManualUpload: true,
              });
            };
            video.src = url;
          } else if (file.type.startsWith('audio/')) {
            useNodeStore.getState().updateNode(nodeId, {
              audioUri: url,
              image: undefined,
              videoUri: undefined,
              isManualUpload: true,
            });
          }
        };

        const fallbackLocal = () => {
          const reader = new FileReader();
          reader.onload = (ev) => applyAsset(ev.target?.result as string);
          reader.readAsDataURL(file);
        };

        // 上传为 fire-and-forget：回调写入的 nodeStore 是全局 store，
        // 画布卸载（切页面）后数据仍在（方案 §5 #10）
        const { currentProjectId } = useProjectStore.getState();
        if (currentProjectId) {
          projectApi
            .uploadAsset(currentProjectId, file, {
              source: 'uploaded',
              node_id: nodeId,
              node_type: NodeType.MEDIA_UPLOAD,
              upload_channel: channel,
            })
            .then((asset) => applyAsset(normalizeAssetUrl(asset.url)))
            .catch((err: any) => {
              console.error('[外部素材上传] 上传到后端失败，回退本地:', err?.message);
              fallbackLocal();
            });
        } else {
          fallbackLocal();
        }
      });
    },
    [onAddNode, screenToFlowPosition],
  );

  return { uploadFilesAtScreen };
}
